import React from 'react';
import { FiExternalLink } from 'react-icons/fi';

import {
  legalDestination,
  type LegalDocumentKey
} from '../authLegalDestinations';

type Props = {
  documentKey: LegalDocumentKey;
  /** Extra classes for the rendered anchor/text (colour, weight, …). */
  className?: string;
};

/**
 * Renders one legal document reference.
 *
 * When the document is hosted somewhere that resolves it renders a real
 * external anchor; when it is not, it renders the document's NAME as plain
 * text. It never renders an anchor to a destination that does not exist —
 * that is the whole point of routing these through `authLegalDestinations`.
 *
 * The anchor's accessible name carries "(opens in a new tab)" because the
 * `target="_blank"` handoff is invisible to a screen-reader or keyboard user
 * otherwise. The arrow glyph is decorative and hidden from assistive tech.
 */
const LegalDocumentLink = ({ documentKey, className = '' }: Props) => {
  const { label, href } = legalDestination(documentKey);

  if (!href) {
    // Unhosted: name the document, promise no destination.
    return <span className={className}>{label}</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 rounded-sm underline-offset-2 hover:underline focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${className}`}
    >
      <span>{label}</span>
      <FiExternalLink aria-hidden="true" focusable="false" size={11} />
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
};

export default LegalDocumentLink;
